import React, { useState, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  IconButton,
  Chip,
  Tooltip,
  Paper,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import ReplayIcon from '@mui/icons-material/Replay';
import styled from '@emotion/styled';
import { useDispatch } from 'react-redux';
import { showSuccessMessage } from '@store/dashboard';
import { uploadImages } from '../../store/upload-images';

const MAX_FILE_SIZE_MB = 10;

export default function FileUploadUI() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadedFileUrl, setUploadedFileUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [fileTooLarge, setFileTooLarge] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState('');

  const dispatch = useDispatch();

  const handleFile = (file) => {
    if (!file) return;
    if (file.size / 1024 / 1024 > MAX_FILE_SIZE_MB) {
      setFileTooLarge(true);
      setSelectedFile(null);
      return;
    }
    setFileTooLarge(false);
    setSelectedFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const onFileDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    handleFile(file);
  };

  const onFileSelectClick = () => {
    fileInputRef.current?.click();
  };

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    handleFile(file);
  };

  const onDeleteFile = () => {
    setSelectedFile(null);
    setPreviewUrl('');
  };

  const onUploadClick = useCallback(async () => {
    if (!selectedFile) return;
    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('folderName', 'img');

      dispatch(uploadImages(formData)).then((data) => {
        if (data.payload && data.payload.status === 'SUCCESS') {
          setUploadedFileUrl(data.payload.message);
          setSelectedFile(null);
        } else {
          setUploadError(data.payload.message);
        }
      });
    } catch (error) {
      setUploadError('Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  }, [selectedFile, dispatch]);

  const onCopyUrl = () => {
    navigator.clipboard.writeText(uploadedFileUrl);
    dispatch(showSuccessMessage('URL copied to clipboard'));
  };

  const onReuploadClick = () => {
    setUploadedFileUrl('');
    setSelectedFile(null);
    setPreviewUrl('');
    setUploadError('');
  };

  const PreviewImage = styled.img`
    width: 100%;
    height: 20rem;
    object-fit: contain;
    border-radius: 0.5rem;
  `;

  return (
    <Paper
      elevation={0}
      onDragOver={(e) => e.preventDefault()}
      onDrop={onFileDrop}
      onClick={onFileSelectClick}>
      <Box flex={1}>
        <Typography
          variant="h3"
          fontWeight={600}
          gutterBottom>
          File Upload
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          mb={2}>
          Select a file to upload or drag it into this area. Max file size:{' '}
          {MAX_FILE_SIZE_MB}MB.
        </Typography>

        {!selectedFile && !uploadedFileUrl && (
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}>
            Select File
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              style={{ display: 'none' }}
              onChange={onFileChange}
            />
          </Button>
        )}

        {fileTooLarge && (
          <Typography
            variant="body2"
            color="error"
            mt={2}>
            File exceeds the 10MB limit. Please select a smaller file.
          </Typography>
        )}

        {uploadError && (
          <Typography
            variant="body2"
            color="error"
            mt={2}>
            {uploadError}
          </Typography>
        )}

        {uploadedFileUrl && (
          <>
            <Typography
              variant="body2"
              color="text.secondary"
              mt={2}>
              File uploaded successfully:
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              mt={1}
              alignItems="center"
              flexWrap="wrap">
              <Chip
                label={uploadedFileUrl}
                variant="outlined"
                sx={{ maxWidth: 300 }}
              />
              <Tooltip title="Copy URL">
                <IconButton onClick={onCopyUrl}>
                  <FileCopyIcon />
                </IconButton>
              </Tooltip>
            </Stack>
            <Button
              variant="outlined"
              startIcon={<ReplayIcon />}
              onClick={onReuploadClick}
              sx={{ mt: 2 }}>
              Upload Another File
            </Button>
          </>
        )}
      </Box>

      {selectedFile && !uploadedFileUrl && (
        <Box
          flex={1}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={2}>
          <PreviewImage
            src={previewUrl}
            alt="Preview"
          />
          <Chip
            icon={<UploadFileIcon />}
            label={selectedFile.name}
            onDelete={onDeleteFile}
            color="primary"
            variant="outlined"
            sx={{ maxWidth: 300 }}
          />
          <Button
            variant="contained"
            onClick={onUploadClick}
            disabled={isUploading || fileTooLarge}
            fullWidth>
            {isUploading ? 'Uploading...' : 'Upload'}
          </Button>
        </Box>
      )}
    </Paper>
  );
}
